import React, { useEffect, useMemo, useRef, useState } from 'react'
import { StyleSheet, View, ScrollView, SafeAreaView, TouchableOpacity } from 'react-native'
import { Text, Icon, Button } from '@ui-kitten/components'
import ProductList from '../../components/ProductList'
import { useAtomValue } from 'jotai'
import { useTranslation } from 'react-i18next'
import { usePostHog } from 'posthog-react-native'
import { productCategoriesDictAtom, productsDictAtom, userRoleAtom, currentStoreSaleSettingsAtom, userStoreIdAtom } from '../../global/AtomsProvider'
import { getProductsForNewSaleByCategoryList } from '../../utils/productListHelpers'
import HeaderIconButton from '../../components/HeaderIconButton'
import { useColors } from '../../utils/useColors'
import { getNewCartItems } from '../../utils/cartItemHelpers'
import { HintView } from '../../components/HintView'
import {
  ADD_CART_ITEM_CATALOG_STATES,
  ADD_CART_ITEM_OUTCOMES,
  captureAddCartItemClosed,
  createAddCartItemTouchTracker,
  getAddCartItemCatalogState,
} from '../../utils/addCartItemCatalog'

export default function AddCartItemScreen({ navigation, route }) {
  const colors = useColors()
  // ATL-565: default to [] (and guard route.params) — a re-mount/restore with no
  // params left initialCartItems undefined → `.every` crashed onDonePress (fatal).
  const { addNewCartItem, store, cartItems: initialCartItems = [] } = route.params || {}
  const [cartItems, setCartItems] = useState([])
  const productsDict = useAtomValue(productsDictAtom)
  const productCategories = useAtomValue(productCategoriesDictAtom)
  const userRole = useAtomValue(userRoleAtom)
  const currentStoreSaleSettings = useAtomValue(currentStoreSaleSettingsAtom)
  // ATL-968: this sorts the whole catalog, and every product tap sets cartItems state —
  // so without the memo a 3,400-product org re-sorts and re-groups its catalog on every
  // tap to produce a list that did not change. Same reason as ProductSearch.js /
  // BatchProductEditScreen.js.
  const productListForNewSale = useMemo(
    () => getProductsForNewSaleByCategoryList(productsDict, productCategories, (userRole?.permissions?.['sellCustomProducts'] || userRole?.id === 'admin'), false, store),
    [productsDict, productCategories, userRole, store]
  )
  const { t } = useTranslation()
  const postHog = usePostHog()
  const userStoreId = useAtomValue(userStoreIdAtom)

  // ACT-UX-2026-001: a new organization's catalog is one seeded sample product, and this screen
  // gave no way to add a real one, so merchants closed it within seconds. Say what is missing and
  // offer the product form. `ProductList` filters by `preselectedStore ?? userStoreId`; classify
  // against the same store so the hint describes the list the merchant is looking at.
  const listStore = store ?? userStoreId
  const catalogState = useMemo(
    () => getAddCartItemCatalogState(productListForNewSale, listStore),
    [productListForNewSale, listStore]
  )
  // The same test the Home menu applies to the Products entry: whoever may open the product list
  // may create a product. Anyone else is told who can.
  const canCreateProduct = Boolean(userRole?.permissions?.['Products'] || userRole?.id === 'admin')

  // One `add_cart_item_closed` per visit, sent on unmount. Refs, because the cleanup must see the
  // values at close time, not the ones captured when the effect was set up.
  const outcomeRef = useRef(ADD_CART_ITEM_OUTCOMES.DISMISSED)
  const createProductTappedRef = useRef(false)
  const catalogStateOnOpenRef = useRef(catalogState)
  const catalogStateRef = useRef(catalogState)
  catalogStateRef.current = catalogState
  const multiSelectRef = useRef(Boolean(currentStoreSaleSettings?.allowMultipleProductSelection))
  multiSelectRef.current = Boolean(currentStoreSaleSettings?.allowMultipleProductSelection)
  const postHogRef = useRef(postHog)
  postHogRef.current = postHog
  // Multi-select (on for every new org) only ADDS on confirm; leaving any other way drops the
  // ticked products without a word. Reported separately so that exact trap can be counted.
  const pendingSelectionCountRef = useRef(0)
  pendingSelectionCountRef.current = getNewCartItems(cartItems, initialCartItems).length
  // ATL-1921: what removed the picker (a native dismissal, the app's own goBack, or the whole
  // navigator going away) and whether product taps reached it at all — see addCartItemCatalog.ts.
  const removalActionRef = useRef(null)
  const itemPressCountRef = useRef(0)
  const gestureCancelCountRef = useRef(0)
  // ATL-1921, second increment: whether category filters were tapped (the merchant's words are
  // "tap a category, then a product, and it throws you back"), and what the finger on the content
  // was doing when the close began — which separates the header back button from a swipe-back.
  const categoryPressCountRef = useRef(0)
  const touchTrackerRef = useRef(null)
  if (touchTrackerRef.current === null) touchTrackerRef.current = createAddCartItemTouchTracker()
  useEffect(() => {
    const unsubscribeBeforeRemove = navigation.addListener('beforeRemove', (e) => {
      removalActionRef.current = e.data?.action ?? null
    })
    const unsubscribeGestureCancel = navigation.addListener('gestureCancel', () => {
      gestureCancelCountRef.current += 1
      // A cancelled swipe-back sends no transitionStart{closing:false} (react-native-screens skips
      // notifyWillAppear while _isSwiping), so this is where that close is called off.
      touchTrackerRef.current.closeAbandoned()
    })
    const unsubscribeTransitionStart = navigation.addListener('transitionStart', (e) => {
      // closing:true is the picker's viewWillDisappear, which a push over it fires too. A native
      // close starts while the picker is still the stack's top route; a push has already put the
      // new route on top, so being covered is not a close.
      if (e.data?.closing && navigation.isFocused()) touchTrackerRef.current.closeStarted()
      else touchTrackerRef.current.closeAbandoned()
    })
    return () => {
      unsubscribeBeforeRemove()
      unsubscribeGestureCancel()
      unsubscribeTransitionStart()
    }
  }, [navigation])
  useEffect(() => () => {
    const outcome = outcomeRef.current === ADD_CART_ITEM_OUTCOMES.DISMISSED && pendingSelectionCountRef.current > 0
      ? ADD_CART_ITEM_OUTCOMES.DISMISSED_WITH_SELECTION
      : outcomeRef.current
    captureAddCartItemClosed(postHogRef.current, {
      outcome,
      catalogStateOnOpen: catalogStateOnOpenRef.current,
      catalogStateOnClose: catalogStateRef.current,
      createProductTapped: createProductTappedRef.current,
      multiSelect: multiSelectRef.current,
      removalAction: removalActionRef.current,
      itemPressCount: itemPressCountRef.current,
      gestureCancelCount: gestureCancelCountRef.current,
      categoryPressCount: categoryPressCountRef.current,
      touch: touchTrackerRef.current.snapshot(),
    })
  }, [])

  const onCategoryPress = () => {
    categoryPressCountRef.current += 1
  }

  const onCreateProductPress = () => {
    createProductTappedRef.current = true
    navigation.navigate('ProductDetail', { productItemProp: null })
  }

  useEffect(() => {
    if (currentStoreSaleSettings?.allowMultipleProductSelection) {
      navigation.setOptions({
        headerRight: () => (
          <HeaderIconButton
            onPress={onDonePress}
            iconName='checkmark-outline'
          />
        ),
      })
    }
  }, [cartItems, currentStoreSaleSettings?.allowMultipleProductSelection])

  const onItemPress = (item, index, store) => {
    itemPressCountRef.current += 1
    if (currentStoreSaleSettings?.allowMultipleProductSelection) {
      setCartItems(prev => {
        const existingIndex = prev.findIndex(p => p.id === item.id)
        if (existingIndex >= 0) {
          return prev.filter(p => p.id !== item.id)
        } else {
          return [...prev, item]
        }
      })
    } else {
      outcomeRef.current = ADD_CART_ITEM_OUTCOMES.PRODUCT_PICKED
      navigation.goBack()
      addNewCartItem(item)
    }
  }

  const onDonePress = () => {
    // ATL-565: getNewCartItems guards against undefined initialCartItems.
    const newItems = getNewCartItems(cartItems, initialCartItems)
    if (newItems.length > 0) outcomeRef.current = ADD_CART_ITEM_OUTCOMES.PRODUCTS_PICKED
    newItems.forEach(item => addNewCartItem(item))
    navigation.goBack()
  }

  const clearSelectedItems = () => {
    setCartItems([])
  }

  return (
    <SafeAreaView
      style={styles.container}
      // ATL-1921: observers only — these props do not take the responder, so every tap, scroll and
      // gesture inside the picker behaves exactly as without them.
      onTouchStart={(e) => touchTrackerRef.current.touchStart(e)}
      onTouchMove={(e) => touchTrackerRef.current.touchMove(e)}
      onTouchEnd={(e) => touchTrackerRef.current.touchEnd(e)}
      onTouchCancel={(e) => touchTrackerRef.current.touchCancel(e)}
    >
      {catalogState !== ADD_CART_ITEM_CATALOG_STATES.HAS_PRODUCTS && (
        <View style={styles.hintContainer} testID={`add-cart-item-hint-${catalogState}`}>
          <HintView
            iconName='info-outline'
            text={
              t(catalogState === ADD_CART_ITEM_CATALOG_STATES.EMPTY ? 'newSale.addCartItemEmptyHint' : 'newSale.addCartItemSampleOnlyHint')
              + (canCreateProduct ? '' : ' ' + t('newSale.addCartItemAskAdmin'))
            }
            action={canCreateProduct ? onCreateProductPress : null}
            callToAction={canCreateProduct ? t('newSale.addCartItemCreateProduct') : null}
          />
        </View>
      )}
      {/*
        ATL-1366: `productListForNewSale` is already filtered, but ProductList's SEARCH box does
        not go through it — it queries the shared all-catalogue index. Without the flag below,
        hiding an ingredient leaves it sellable from this screen forever, which is what Марракеш
        reported: «Молоко концентрированное» kept coming back one search at a time.
      */}
      <ProductList 
        preselectedStore={store} 
        products={productListForNewSale} 
        onItemPress={onItemPress}
        excludeHiddenInNewSale={true}
        allowMultipleSelection={currentStoreSaleSettings?.allowMultipleProductSelection}
        selectedProducts={cartItems}
        onCategoryPress={onCategoryPress}
        links={canCreateProduct ? [{
          text: t('newSale.addCartItemNewProduct'),
          icon: 'plus-outline',
          action: onCreateProductPress,
          testID: 'add-cart-item-new-product-link',
        }] : null}
      />
      {currentStoreSaleSettings?.allowMultipleProductSelection && cartItems.length > 0 && (
        <View style={[styles.selectedItemsContainer, { backgroundColor: colors.white }]}>
          <Text category='s1' style={styles.selectedItemsCount}>{cartItems.length + ':'}</Text>
          <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.scrollView}>
            {cartItems.map((item, index) => (
              <View key={item.id} style={styles.selectedItem}>
                <Text category='s2'>{item.name + (index < cartItems.length - 1 ? ', ' : '')}</Text>
              </View>
            ))}
          </ScrollView>
          <TouchableOpacity onPress={clearSelectedItems} style={styles.clearButton}>
            <Icon name='close-outline' width={24} height={24} fill={colors.primary} />
          </TouchableOpacity>
          {/*
            ACT-UX-2026-001: the header checkmark was the only way to confirm a multi-selection, and
            a new merchant does not read a bare icon as "add to the sale". This is the same action,
            next to what it acts on.
          */}
          <Button size='small' style={styles.confirmButton} onPress={onDonePress} testID='add-cart-item-confirm-selection'>
            {t('newSale.addSelectedToCart')}
          </Button>
        </View>
      )}
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    alignItems: 'stretch',
  },
  hintContainer: {
    paddingHorizontal: 20,
    paddingTop: 10,
  },
  selectedItemsContainer: {
    position: 'absolute',
    bottom: 20,
    left: 20,
    right: 20,
    borderRadius: 20,
    padding: 10,
    flexDirection: 'row',
    alignItems: 'center',
    shadowColor: "#000",
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.25,
    shadowRadius: 3.84,
    elevation: 5,
  },
  selectedItem: {
    // Styles remain the same
  },
  selectedItemsCount: {
    marginRight: 7,
    marginLeft: 5,
    fontWeight: 'bold',
  },
  scrollView: {
    flex: 1,
  },
  clearButton: {
    padding: 5,
  },
  confirmButton: {
    marginLeft: 5,
  },
});
